import React from "react";
import {
  BarChart3,
  Check,
  Crown,
  Layers3,
  Mail,
  Sparkles,
  UsersRound,
  WandSparkles,
  ArrowRight,
  Clock,
  Award,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

import brandLogo from "@/assets/aiv-logo-trans.png";
import HomeHeroSlider from "@/components/marketing/HomeHeroSlider";
import HomeNavbar from "@/components/marketing/HomeNavbar";
import Reveal from "@/components/marketing/Reveal";
import { useI18n } from "@/contexts/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const LANDING_THEME_KEY = "clubpilot_landing_theme";

type TranslatedFeatureCard = {
  icon: LucideIcon;
  title: string;
  text: string;
  bullets: string[];
};

type TranslatedBenefit = {
  icon: LucideIcon;
  title: string;
  description: string;
};

type TranslatedPricingPlan = {
  icon: LucideIcon;
  name: string;
  price: string;
  featured: boolean;
  featuredLabel: string;
  yearlyDiscountPct: number;
  description: string;
  features: string[];
};

const featureCards = [
  {
    icon: UsersRound,
    titleKey: "landing_feature_members_title",
    titleFallback: "Member Management",
    textKey: "landing_feature_members_text",
    textFallback:
      "Bring every player, coach, and admin into one organized member system with smooth onboarding, clear access control, and a structure that scales with your club.",
    bullets: [
      { key: "landing_feature_members_bullet_1", fallback: "Single and bulk member onboarding" },
      { key: "landing_feature_members_bullet_2", fallback: "Role and access management" },
      { key: "landing_feature_members_bullet_3", fallback: "First-time login invitation flow" },
    ],
  },
  {
    icon: Layers3,
    titleKey: "landing_feature_queue_title",
    titleFallback: "Queue & Court Flow",
    textKey: "landing_feature_queue_text",
    textFallback:
      "Run queue movement and court assignment from one live control surface built for busy sessions, balanced rotations, and smooth match operations.",
    bullets: [
      { key: "landing_feature_queue_bullet_1", fallback: "Live queue visibility" },
      { key: "landing_feature_queue_bullet_2", fallback: "Multiple court assignment modes" },
      { key: "landing_feature_queue_bullet_3", fallback: "Match finishing and automatic queue updates" },
    ],
  },
  {
    icon: BarChart3,
    titleKey: "landing_feature_stats_title",
    titleFallback: "Stats & Insights",
    textKey: "landing_feature_stats_text",
    textFallback:
      "Turn club activity into clear performance visibility with match history, leaderboard movement, reporting, evaluations, and AI-provided insights your team can act on.",
    bullets: [
      { key: "landing_feature_stats_bullet_1", fallback: "Leaderboard tracking and match history" },
      { key: "landing_feature_stats_bullet_2", fallback: "Daily reporting and weekly insights" },
      { key: "landing_feature_stats_bullet_3", fallback: "Evaluation and performance analytics" },
    ],
  },
] as const;

const pricingPlans = [
  {
    icon: Sparkles,
    nameKey: "pkg_name_starter",
    nameFallback: "Starter",
    price: "CA$69",
    featured: false,
    featuredLabelKey: "pkg_tier_1",
    featuredLabelFallback: "Package 1",
    yearlyDiscountPct: 5,
    descriptionKey: "landing_pricing_starter_desc",
    descriptionFallback: "For clubs that need a clean operational base.",
    features: [
      { key: "pkg_feature_50_members", fallback: "Up to 50 active members" },
      { key: "pkg_feature_member_mgmt", fallback: "Member management" },
      { key: "pkg_feature_queue_courts", fallback: "Queue and court assignment" },
      { key: "pkg_feature_basic_leaderboard", fallback: "Match history and basic leaderboard" },
      { key: "pkg_feature_basic_dashboard", fallback: "Basic admin dashboard" },
    ],
  },
  {
    icon: WandSparkles,
    nameKey: "pkg_name_pro",
    nameFallback: "Pro",
    price: "CA$159",
    featured: true,
    featuredLabelKey: "pkg_tier_2",
    featuredLabelFallback: "Package 2",
    yearlyDiscountPct: 8,
    descriptionKey: "landing_pricing_pro_desc",
    descriptionFallback: "For established clubs that want deeper performance visibility.",
    features: [
      { key: "pkg_feature_everything_starter", fallback: "Everything in Starter" },
      { key: "pkg_feature_150_members", fallback: "Up to 150 active members" },
      { key: "pkg_feature_coach_eval", fallback: "Coach evaluations" },
      { key: "pkg_feature_equipment", fallback: "Equipment tracking" },
      { key: "landing_pkg_feature_ml_advanced", fallback: "Advanced ML-assisted analytics for key performance trends" },
      { key: "pkg_feature_dashboard_insights", fallback: "Actionable performance insights on your dashboard" },
      { key: "pkg_feature_daily_advanced", fallback: "Daily match reports and advanced leaderboard trends" },
    ],
  },
  {
    icon: Crown,
    nameKey: "pkg_name_elite",
    nameFallback: "Elite",
    price: "CA$299",
    featured: false,
    featuredLabelKey: "pkg_tier_3",
    featuredLabelFallback: "Package 3",
    yearlyDiscountPct: 10,
    descriptionKey: "landing_pricing_elite_desc",
    descriptionFallback: "For clubs that want advanced control, branding, and AI tooling.",
    features: [
      { key: "pkg_feature_everything_pro", fallback: "Everything in Pro" },
      { key: "pkg_feature_250_members", fallback: "Up to 250 active members" },
      { key: "pkg_feature_multi_admin", fallback: "Advanced roles and permissions (multiple club admins)" },
      { key: "pkg_feature_ui_branding", fallback: "Advanced UI controls for club branding" },
      { key: "pkg_feature_logo", fallback: "Club logo upload and management" },
      { key: "pkg_feature_avatar", fallback: "Member profile avatar selection" },
      { key: "pkg_feature_ai_chat", fallback: "AI chat for match-history insights" },
      { key: "pkg_feature_dashboard_insights", fallback: "Actionable performance insights on your dashboard" },
    ],
  },
] as const;

const benefits = [
  { icon: Zap, titleKey: "landing_benefit_1_title", titleFallback: "Built for Live Club Sessions", descriptionKey: "landing_benefit_1_text", descriptionFallback: "Keep members, queue flow, court assignment, and match handling moving without operational lag." },
  { icon: Award, titleKey: "landing_benefit_2_title", titleFallback: "Structured for Real Club Work", descriptionKey: "landing_benefit_2_text", descriptionFallback: "Built around the way real clubs operate, with tools that support daily coordination instead of generic admin overhead." },
  { icon: Clock, titleKey: "landing_benefit_3_title", titleFallback: "Ready for Daily Operations", descriptionKey: "landing_benefit_3_text", descriptionFallback: "Support onboarding, match control, reporting, and performance review from one dependable system." },
] as const;

function useLandingTheme() {
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const saved = window.localStorage.getItem(LANDING_THEME_KEY);
    const nextIsDark = saved === "dark";
    setIsDark(nextIsDark);
  }, []);

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    window.localStorage.setItem(LANDING_THEME_KEY, isDark ? "dark" : "light");
  }, [isDark]);

  return {
    isDark,
    toggleTheme: () => setIsDark((prev) => !prev),
  };
}

function SectionIntro({
  badge,
  title,
  description,
  isDarkMode,
  isTealBg,
}: {
  badge: string;
  title: string;
  description: string;
  isDarkMode: boolean;
  isTealBg: boolean;
}) {
  const badgeClass = isDarkMode
    ? "border-gray-800 bg-gray-900 text-gray-300"
    : isTealBg
      ? "border-white/20 bg-white/10 text-white"
      : "border-[#20c5b5]/20 bg-[#20c5b5]/10 text-[#20c5b5]";

  const titleClass = isDarkMode || isTealBg ? "text-white" : "text-gray-900";
  const descClass = isDarkMode ? "text-gray-400" : isTealBg ? "text-white/85" : "text-gray-600";

  return (
    <div className="mx-auto max-w-3xl text-center">
      <Badge className={`rounded-full px-3 py-1 text-xs font-medium sm:px-4 sm:py-1.5 sm:text-sm ${badgeClass}`}>
        {badge}
      </Badge>
      <h2 className={`mt-4 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl ${titleClass}`}>
        {title}
      </h2>
      <p className={`mx-auto mt-3 max-w-2xl text-sm leading-relaxed sm:text-base ${descClass}`}>
        {description}
      </p>
    </div>
  );
}

function FeatureCard({
  card,
  index,
  isDarkMode,
  isTealBg,
}: {
  card: TranslatedFeatureCard;
  index: number;
  isDarkMode: boolean;
  isTealBg: boolean;
}) {
  const cardClass = isDarkMode
    ? "border-gray-800 bg-gray-900"
    : isTealBg
      ? "border-white/20 bg-white/95"
      : "border-gray-200 bg-white";

  const titleClass = isDarkMode ? "text-white" : "text-gray-900";
  const textClass = isDarkMode ? "text-gray-400" : "text-gray-600";
  const iconWrapClass = isDarkMode ? "bg-gray-800 text-gray-300" : "bg-[#20c5b5]/10 text-[#20c5b5]";

  return (
    <Reveal delayMs={index * 90}>
      <Card className={`h-full rounded-2xl border shadow-lg transition-all duration-500 hover:-translate-y-1 hover:shadow-xl ${cardClass}`}>
        <CardContent className="p-6 sm:p-7">
          <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl ${iconWrapClass}`}>
            <card.icon className="h-5 w-5" />
          </div>
          <h3 className={`text-xl font-semibold ${titleClass}`}>{card.title}</h3>
          <p className={`mt-3 text-sm leading-relaxed ${textClass}`}>{card.text}</p>
          <ul className="mt-5 space-y-3">
            {card.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2.5 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#20c5b5]" />
                <span className={textClass}>{bullet}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </Reveal>
  );
}

function BenefitCard({
  benefit,
  index,
  isDarkMode,
}: {
  benefit: TranslatedBenefit;
  index: number;
  isDarkMode: boolean;
}) {
  if (isDarkMode) {
    return (
      <Reveal key={benefit.title} delayMs={index * 100}>
        <Card className="h-full rounded-2xl border border-gray-800 bg-gray-900 shadow-lg transition-all duration-500 hover:-translate-y-1 hover:shadow-xl">
          <CardContent className="p-6 sm:p-7">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#20c5b5]">
              <benefit.icon className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white">{benefit.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-400">{benefit.description}</p>
          </CardContent>
        </Card>
      </Reveal>
    );
  }

  return (
    <Reveal key={benefit.title} delayMs={index * 100}>
      <div className="group text-center transition-all duration-300 hover:-translate-y-1">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#20c5b5] transition-all duration-300 group-hover:scale-110">
          <benefit.icon className="h-7 w-7 text-white" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-gray-900 md:text-xl">{benefit.title}</h3>
        <p className="text-sm text-gray-600">{benefit.description}</p>
      </div>
    </Reveal>
  );
}

export default function Home() {
  const { t } = useI18n();
  const { isDark, toggleTheme } = useLandingTheme();
  const localizedFeatureCards = React.useMemo<TranslatedFeatureCard[]>(
    () =>
      featureCards.map((card) => ({
        icon: card.icon,
        title: t(card.titleKey, card.titleFallback),
        text: t(card.textKey, card.textFallback),
        bullets: card.bullets.map((bullet) => t(bullet.key, bullet.fallback)),
      })),
    [t],
  );
  const localizedBenefits = React.useMemo<TranslatedBenefit[]>(
    () =>
      benefits.map((benefit) => ({
        icon: benefit.icon,
        title: t(benefit.titleKey, benefit.titleFallback),
        description: t(benefit.descriptionKey, benefit.descriptionFallback),
      })),
    [t],
  );
  const localizedPricingPlans = React.useMemo<TranslatedPricingPlan[]>(
    () =>
      pricingPlans.map((plan) => ({
        icon: plan.icon,
        name: t(plan.nameKey, plan.nameFallback),
        price: plan.price,
        featured: plan.featured,
        featuredLabel: t(plan.featuredLabelKey, plan.featuredLabelFallback),
        yearlyDiscountPct: plan.yearlyDiscountPct,
        description: t(plan.descriptionKey, plan.descriptionFallback),
        features: plan.features.map((feature) => t(feature.key, feature.fallback)),
      })),
    [t],
  );

  return (
    <div className="min-h-screen bg-white text-foreground dark:bg-gray-950">
      <HomeNavbar isDark={isDark} onToggleTheme={toggleTheme} />

      <main>
        <HomeHeroSlider />

        {/* Features Section - Teal Background */}
        <section id="features" className={`px-4 py-16 sm:py-20 lg:px-6 lg:py-24 ${isDark ? "bg-gray-950" : "bg-[#20c5b5]"}`}>
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <SectionIntro
                badge={t("landing_features_badge", "Core Features")}
                title={t("landing_features_title", "Everything your club needs")}
                description={t(
                  "landing_features_desc",
                  "Built specifically for racket sports clubs - from member management to match analytics.",
                )}
                isDarkMode={isDark}
                isTealBg={!isDark}
              />
            </Reveal>
            <div className="mt-10 grid gap-4 sm:mt-12 sm:gap-5 md:mt-14 md:gap-6 lg:grid-cols-3">
              {localizedFeatureCards.map((card, index) => (
                <FeatureCard key={card.title} card={card} index={index} isDarkMode={isDark} isTealBg={!isDark} />
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section - White Background with Cards */}
        <section className={`px-4 py-16 sm:py-20 lg:px-6 lg:py-24 ${isDark ? "bg-gray-950" : "bg-white"}`}>
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <SectionIntro
                badge={t("landing_benefits_badge", "Why Choose Us")}
                title={t("landing_benefits_title", "Built for modern racket sports clubs")}
                description={t(
                  "landing_benefits_desc",
                  "A focused operating system for clubs that need structure, speed, and better control across members, courts, matches, and reporting.",
                )}
                isDarkMode={isDark}
                isTealBg={false}
              />
            </Reveal>
            <div className="mt-10 grid gap-6 sm:mt-12 sm:grid-cols-2 sm:gap-8 md:mt-14 lg:grid-cols-3">
              {localizedBenefits.map((benefit, index) => (
                <BenefitCard key={benefit.title} benefit={benefit} index={index} isDarkMode={isDark} />
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className={`px-4 py-16 sm:py-20 lg:px-6 lg:py-24 ${isDark ? "bg-gray-950" : "bg-[#20c5b5]"}`}>
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <SectionIntro
                badge={t("landing_pricing_badge", "Pricing")}
                title={t("landing_pricing_title", "Choose your package")}
                description={t(
                  "landing_pricing_desc",
                  "Select the plan that fits your club's needs and scale as you grow.",
                )}
                isDarkMode={isDark}
                isTealBg={!isDark}
              />
            </Reveal>
            <div className="mt-10 grid gap-5 sm:mt-12 sm:gap-6 md:mt-14 md:gap-8 xl:grid-cols-3">
              {localizedPricingPlans.map((plan, index) => (
                <Reveal key={plan.name} delayMs={index * 90}>
                    <div className={`relative h-full transition-all duration-300 hover:-translate-y-2 ${
                      plan.featured ? "md:-mt-4" : ""
                    }`}>
                    {plan.featured && (
                      <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2">
                        <Badge className="rounded-full bg-[#f97415] px-3 py-0.5 text-xs font-medium text-white shadow-md sm:px-4 sm:py-1">
                          {t("landing_pricing_most_popular", "Most Popular")}
                        </Badge>
                      </div>
                    )}
                      <Card className={`h-full overflow-hidden rounded-2xl border shadow-lg transition-all duration-500 hover:shadow-2xl sm:rounded-[28px] ${
                        isDark
                          ? "bg-gray-900 border-gray-800"
                          : plan.featured
                            ? "bg-white border-[#f97415]/40"
                            : "bg-white border-gray-200"
                      }`}>
                        <CardContent className="flex h-full flex-col p-0">
                        <div className={`border-b px-5 py-5 sm:px-6 sm:py-6 md:px-8 ${isDark ? "border-gray-800" : "border-gray-100"}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className={`flex h-11 w-11 items-center justify-center rounded-xl shadow-sm sm:h-12 sm:w-12 ${
                              isDark
                                ? "bg-gray-800 text-gray-300"
                                : plan.featured
                                  ? "bg-[#20c5b5]/10 text-[#20c5b5]"
                                  : "bg-gray-100 text-[#20c5b5]"
                            }`}>
                              <plan.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>
                            <Badge className={`rounded-full px-2.5 py-0.5 text-xs sm:px-3 ${
                              isDark
                                ? "bg-gray-800 text-gray-300"
                                : plan.featured
                                  ? "bg-[#20c5b5]/10 text-[#20c5b5]"
                                  : "bg-gray-100 text-gray-600"
                            }`}>
                              {plan.featuredLabel}
                            </Badge>
                          </div>
                          <div className="mt-5">
                            <h3 className={`text-xl font-bold sm:text-2xl ${isDark ? "text-white" : "text-gray-900"}`}>
                              {plan.name}
                            </h3>
                            <p className={`mt-2 text-sm leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                              {plan.description}
                            </p>
                            <p className={`mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] sm:text-xs ${isDark ? "text-gray-500" : "text-[#20c5b5]"}`}>
                              {t("landing_pricing_save_yearly", "Save {{pct}}% on yearly billing").replace("{{pct}}", String(plan.yearlyDiscountPct))}
                            </p>
                          </div>
                          <div className="mt-5 flex items-end">
                            <span className={`text-3xl font-bold sm:text-4xl ${isDark ? "text-white" : "text-gray-900"}`}>
                              {plan.price}
                            </span>
                            <span className={`ml-2 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                              /{t("pkg_month", "month")}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-1 flex-col px-5 py-5 sm:px-6 sm:py-6 md:px-8">
                        <div className={`flex-1 rounded-2xl border p-4 sm:p-5 ${
                          isDark ? "border-gray-800 bg-gray-950/60" : "border-gray-200 bg-gray-50"
                        }`}>
                        <ul className="space-y-2.5 sm:space-y-3">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2.5 text-sm">
                              <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isDark ? "text-gray-500" : "text-[#20c5b5]"}`} />
                              <span className={isDark ? "text-gray-300" : "text-gray-700"}>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        </div>
                        <Button
                          asChild
                          className={`group mt-6 w-full rounded-xl text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.01] sm:text-base ${
                            plan.featured 
                              ? "bg-[#f97415] text-white shadow-md hover:bg-[#f97415] hover:text-white hover:shadow-xl" 
                              : isDark
                                ? "border-gray-700 bg-gray-800 text-white hover:bg-gray-800 hover:text-white hover:shadow-lg"
                                : "border-gray-200 bg-gray-50 text-gray-900 hover:bg-gray-50 hover:text-gray-900 hover:shadow-lg"
                          }`}
                          variant={plan.featured ? "default" : "outline"}
                        >
                          <Link to="/signup" className="flex w-full items-center justify-center gap-2">
                            <span>{t("landing_pricing_choose_plan", "Choose {{name}}").replace("{{name}}", plan.name)}</span>
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </Link>
                        </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`px-4 py-16 sm:py-20 lg:px-6 lg:py-24 ${isDark ? "bg-gray-950" : "bg-white"}`}>
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <div className={`group relative overflow-hidden rounded-2xl p-8 text-center shadow-xl transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl sm:rounded-3xl sm:p-12 ${
                isDark ? "bg-gray-900" : "bg-[#20c5b5]"
              }`}>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(32,197,181,0.1),transparent_70%)] transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#20c5b5]/50 to-transparent" />
                <h2 className="relative text-2xl font-bold text-white sm:text-3xl lg:text-4xl">
                  {t("landing_cta_title", "Give your club one system for members, courts, matches, and insight.")}
                </h2>
                <p className={`relative mx-auto mt-2 max-w-2xl text-sm sm:mt-3 sm:text-base ${isDark ? "text-gray-400" : "text-white/90"}`}>
                  {t(
                    "landing_cta_desc",
                    "Launch with a cleaner operational setup, reduce admin friction, and give players, coaches, and club staff a more professional experience from day one.",
                  )}
                </p>
                <div className="relative mt-6 flex flex-col justify-center gap-3 sm:mt-8 sm:flex-row sm:gap-4">
                  <Button asChild size="lg" className="group rounded-lg bg-[#f97415] px-6 py-5 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] hover:bg-[#f97415] hover:shadow-2xl sm:px-8 sm:text-base">
                    <Link to="/signup" className="flex items-center gap-2">
                      {t("landing_cta_button", "Create Your Club")}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className={`px-4 pb-16 pt-10 sm:pb-20 sm:pt-16 lg:px-6 lg:pb-24 lg:pt-20 ${isDark ? "bg-gray-950" : "bg-[#20c5b5]"}`}>
          <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1fr_0.92fr] lg:gap-8">
            <Reveal>
              <div className={`rounded-xl p-6 shadow-lg sm:rounded-2xl sm:p-8 ${
                isDark ? "bg-gray-900" : "bg-white"
              }`}>
                  <Badge className={`rounded-full px-3 py-1 text-xs ${
                    isDark ? "bg-gray-800 text-gray-400 border-gray-700" : "bg-[#20c5b5]/10 text-[#20c5b5] border-[#20c5b5]/20"
                  }`}>
                    {t("landing_contact_badge", "Contact Us")}
                  </Badge>
                  <h2 className={`mt-4 text-xl font-bold tracking-tight sm:mt-5 sm:text-2xl md:text-3xl ${isDark ? "text-white" : "text-gray-900"}`}>
                    {t("landing_contact_title", "Talk to us about the right setup for your club.")}
                  </h2>
                  <p className={`mt-3 max-w-2xl text-xs leading-relaxed sm:mt-4 sm:text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {t(
                      "landing_contact_desc",
                      "Whether you are launching a new venue, replacing scattered admin work, or tightening operations across members, courts, reporting, and analytics, we can help you choose the right package and rollout approach.",
                    )}
                  </p>
                <div className="mt-5 flex flex-col gap-3 sm:mt-6 sm:flex-row sm:gap-4">
                  <Button asChild size="lg" className="rounded-lg bg-[#f97415] px-5 py-5 text-sm font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-[#ef4343] sm:px-6">
                    <a href="mailto:support@aivstudios.com">
                      <Mail className="mr-2 h-4 w-4" />
                      support@aivstudios.com
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className={`rounded-lg px-5 py-5 text-sm font-semibold transition-all duration-300 hover:scale-105 sm:px-6 ${
                    isDark 
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white" 
                      : "border-[#20c5b5]/20 text-[#20c5b5] hover:bg-[#20c5b5]/5 hover:text-[#20c5b5]"
                  }`}>
                    <Link to="/signup">{t("landing_hero_create_club", "Create your club")}</Link>
                  </Button>
                </div>
              </div>
            </Reveal>

            <Reveal delayMs={120}>
              <div className="space-y-4 sm:space-y-5">
                <Card className={`rounded-xl shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl sm:rounded-2xl ${
                  isDark ? "bg-gray-900 border-gray-800" : "bg-white"
                }`}>
                  <CardContent className="p-5 sm:p-6">
                      <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-[#20c5b5]"}`}>
                        {t("landing_contact_best_fit_title", "Best fit")}
                      </p>
                      <p className={`mt-2 text-xs leading-relaxed sm:mt-3 sm:text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        {t(
                          "landing_contact_best_fit_desc",
                          "Clubs that want a more structured operating system across member management, queue control, live court assignment, reporting, evaluations, and insight.",
                        )}
                      </p>
                  </CardContent>
                </Card>
                <Card className={`rounded-xl shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl sm:rounded-2xl ${
                  isDark ? "bg-gray-900 border-gray-800" : "bg-white"
                }`}>
                  <CardContent className="p-5 sm:p-6">
                      <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? "text-gray-400" : "text-[#20c5b5]"}`}>
                        {t("landing_contact_discuss_title", "What you can discuss")}
                      </p>
                      <ul className="mt-3 space-y-2 text-xs leading-relaxed sm:mt-4 sm:space-y-3 sm:text-sm">
                        <li className="flex items-start gap-2 sm:gap-3">
                          <Check className={`mt-0.5 h-3 w-3 sm:h-4 sm:w-4 ${isDark ? "text-gray-500" : "text-[#20c5b5]"}`} />
                          <span className={isDark ? "text-gray-400" : "text-gray-600"}>{t("landing_contact_discuss_1", "Package guidance across Starter, Pro, and Elite")}</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <Check className={`mt-0.5 h-3 w-3 sm:h-4 sm:w-4 ${isDark ? "text-gray-500" : "text-[#20c5b5]"}`} />
                          <span className={isDark ? "text-gray-400" : "text-gray-600"}>{t("landing_contact_discuss_2", "Member migration, onboarding flow, and access structure")}</span>
                        </li>
                        <li className="flex items-start gap-2 sm:gap-3">
                          <Check className={`mt-0.5 h-3 w-3 sm:h-4 sm:w-4 ${isDark ? "text-gray-500" : "text-[#20c5b5]"}`} />
                          <span className={isDark ? "text-gray-400" : "text-gray-600"}>{t("landing_contact_discuss_3", "Operational setup for courts, queue rules, reporting, and coaching workflows")}</span>
                        </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className={`border-t px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12 ${
        isDark ? "border-gray-800 bg-gray-950" : "border-gray-200 bg-white"
      }`}>
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
            <div className="max-w-md">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg p-1.5 shadow-md sm:h-12 sm:w-12 ${
                  isDark ? "bg-gray-800" : "bg-gray-100"
                }`}>
                  <img src={brandLogo} alt="Smashlytics" className="h-full w-full object-contain" />
                </div>
                <div>
                  <p className={`text-base font-semibold sm:text-lg ${isDark ? "text-white" : "text-gray-900"}`}>
                    Smashlytics
                  </p>
                  <p className={`text-[8px] uppercase tracking-wider sm:text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {t("landing_footer_subtitle", "Racket Sports Intelligence System")}
                  </p>
                </div>
              </div>
              <p className={`mt-3 text-xs leading-relaxed sm:mt-4 sm:text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {t(
                  "landing_footer_desc",
                  "Smashlytics brings member operations, court flow, reporting, and performance visibility into one clear system built for modern racket sports organizations.",
                )}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 sm:gap-8">
              <div className="space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <p className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{t("landing_footer_platform", "Platform")}</p>
                <a href="#features" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>{t("landing_nav_features", "Features")}</a>
                <a href="#pricing" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>{t("landing_nav_pricing", "Pricing")}</a>
                <a href="#contact" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>{t("landing_nav_contact", "Contact")}</a>
              </div>
              <div className="space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <p className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{t("landing_footer_access", "Access")}</p>
                <Link to="/login" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>{t("landing_login", "Login")}</Link>
                <Link to="/signup" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>{t("landing_footer_signup", "Signup")}</Link>
                <Link to="/privacy-policy" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>{t("privacy_policy_title", "Privacy Policy")}</Link>
                <Link to="/terms-and-conditions" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>{t("terms_title", "Terms")}</Link>
              </div>
              <div className="space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <p className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{t("landing_footer_talk", "Talk to us")}</p>
                <a href="mailto:support@aivstudios.com" className={`block transition-colors ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-[#20c5b5]"}`}>support@aivstudios.com</a>
                <p className={isDark ? "text-gray-500" : "text-gray-500"}>{t("landing_footer_note", "For clubs planning a cleaner operating model.")}</p>
              </div>
            </div>
          </div>
          <div className={`mt-8 border-t pt-6 text-center text-xs sm:mt-10 sm:pt-8 sm:text-sm ${
            isDark ? "border-gray-800 text-gray-500" : "border-gray-200 text-gray-600"
          }`}>
            <p>{t("landing_footer_copyright", "© {{year}} Smashlytics. Built for modern racket sports organizations.").replace("{{year}}", String(new Date().getFullYear()))}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
